// THE 10-SECOND LOOP's feedback wiring — every game event that deserves a sound or a flourish, in one place:
//   a paw click → the coin-pop (pitch climbing with the combo) · a row filled → the chime · past a full field → the gold ting
//   the combo tops out → a ding; its window lapses → a soft falling wah (and the HUD meter drains)
//   the season turns → its sting + a sweep of motes over every paddy · the harvest → the fanfare
// Sounds follow the music panel's mute and volume (a muted game is silent). The scene work (blade bumps, the marker, the
// gold shimmer) lives in scene/riceField.js; the HUD (meter, count, floating numbers) in ui/hud.js.

import { Events, emit, on } from '../ui/eventBus.js'
import * as sfx from './sfx.js'
import { playEvent } from './sfxMap.js'
import * as season from './seasonManager.js'
import { getMusic } from './musicManager.js'
import { ComboConfig, LoopFeedback, CrowConfig, GlintConfig } from '../config/balance.js'
import { postMessage } from '../ui/messageRail.js'
import { pushLog } from '../ui/hud.js'

let timer = null

let crowTipShown = false // (the first crow of a session explains itself on the rail)
export function init({ rice = null, fx = null } = {}) {
  // level: silent when the music panel is muted; otherwise a floor plus the music volume (sounds stay audible at low music)
  sfx.setLevelSource(() => { const m = getMusic(); if (m.muted) return 0; return 0.45 + 0.55 * (m.volume ?? 1) })

  // (every sound goes through the sound map — game/sfxMap.js — so an assignment made on the Testing Field is what plays)
  on(Events.Click, ({ combo = 0 }) => playEvent('click', combo))
  on(Events.RowFilled, () => playEvent('rowFilled'))
  on(Events.Surplus, ({ surplus }) => { if (surplus % LoopFeedback.surplusTingEvery === 0) playEvent('surplus', surplus) })
  on(Events.ComboChange, ({ max, broke }) => { if (max) playEvent('comboMax'); if (broke) playEvent('comboBreak') })
  on(Events.Harvest, ({ harvestable }) => { if (harvestable > 0) playEvent('harvest') })
  on(Events.MessageShown, () => playEvent('message'))
  on(Events.IdleWarn, () => playEvent('emperor'))
  // the field's creatures
  on(Events.GlintUp, () => playEvent('glintUp'))
  on(Events.GlintCaught, ({ boostClicks, boostMult }) => { playEvent('glintCaught'); pushLog(`✨ You caught a golden glint — your next ${boostClicks} clicks pay ×${boostMult}.`) })
  on(Events.CitizenTended, () => playEvent('coin'))
  on(Events.CitizenKneels, () => playEvent('kneel'))
  on(Events.CrowLand, () => {
    playEvent('crowLand')
    if (!crowTipShown) { crowTipShown = true; postMessage({ key: 'crow-tip', kind: 'info', type: 'CROW', icon: '🐦', title: 'A crow on your rice', text: `Click it before it eats — it has ${Math.round(CrowConfig.sitMs / 1000)} seconds to feed, and a notch of this season's rice goes with it.`, lifeMs: 8000 }) }
    pushLog('🐦 A crow landed on your rice — click it before it eats a notch.')
  })
  on(Events.CrowShooed, () => { playEvent('crowShoo'); pushLog('🐦 You shooed the crow — the row is safe.') })
  on(Events.CrowAte, ({ lost }) => { playEvent('crowAte'); pushLog(`🐦 The crow ate ${lost} notch${lost === 1 ? '' : 'es'} of your rice (−${lost} click${lost === 1 ? '' : 's'} this season). Click crows before they feed.`); postMessage({ key: 'crow-ate', kind: 'info', type: 'CROW', icon: '🐦', title: 'The crow fed', text: `A notch of this season's rice is gone (−${lost} click). Click a crow the moment it lands.`, lifeMs: 7000 }) })
  on(Events.SeasonChange, ({ season: s, previous }) => {
    if (!previous) return // (the first paint at boot is not a turn)
    playEvent(`season${String(s).toUpperCase()}`)
    if (rice && fx) for (const c of rice.paddyCentres()) fx.burst(c.x, 0.3, c.z, 6) // the sweep across the field (the rows bump in riceField)
  })

  // The combo's window lapsing is not a click, so nobody else sees it: watch it here, tell the HUD (the meter drains) and play the break once.
  let wasLive = false, lastValue = 0
  const watch = () => {
    const c = season.getCombo()
    if (wasLive && !c.live && lastValue >= 0.3) emit(Events.ComboChange, { value: c.value, mult: c.mult, live: false, max: false, broke: true })
    else if (!c.live && c.value > 0) emit(Events.ComboChange, { value: c.value, mult: c.mult, live: false, max: false, broke: false })
    else if (!c.live && c.value === 0 && lastValue > 0) emit(Events.ComboChange, { value: 0, mult: 1, live: false, max: false, broke: false })
    wasLive = c.live; lastValue = c.value
  }
  if (timer) clearInterval(timer)
  timer = setInterval(watch, 100)
  if (import.meta.env?.DEV) window.__loop = { combo: () => season.getCombo(), cfg: ComboConfig, resetCrowTip: () => { crowTipShown = false } }
}
